import {useCallback, useEffect, useState} from 'react';

const EMPTY_STATE: Record<string, any> = {
  encounters: [],
  lastDoc: null,
  hasMore: false,
  isLoading: false,
  loadingMore: false,
  error: null,
  hasLoaded: false,
};

const PAGE_CACHE = new Map();
const PAGE_CACHE_TTL_MS = 5 * 60 * 1000;
const PAGE_CACHE_LIMIT = 20;

export const clearPlannerEncounterHistoryPageCache = () => {
  PAGE_CACHE.clear();
};

const writePlannerEncounterHistoryPageCache = (
  key: string,
  page: Record<string, any>,
) => {
  if (!key) return;
  if (PAGE_CACHE.has(key)) PAGE_CACHE.delete(key);
  PAGE_CACHE.set(key, {
    encounters: page.encounters || [],
    cachedAt: Date.now(),
    lastDoc: page.lastDoc || null,
    hasMore: Boolean(page.hasMore),
  });
  while (PAGE_CACHE.size > PAGE_CACHE_LIMIT) {
    const oldestKey = PAGE_CACHE.keys().next().value;
    if (!oldestKey) break;
    PAGE_CACHE.delete(oldestKey);
  }
};

const readPlannerEncounterHistoryPageCache = (key: string) => {
  if (!key) return null;
  const cached = PAGE_CACHE.get(key);
  if (!cached) return null;
  if (Date.now() - (cached.cachedAt || 0) >= PAGE_CACHE_TTL_MS) {
    PAGE_CACHE.delete(key);
    return null;
  }
  PAGE_CACHE.delete(key);
  PAGE_CACHE.set(key, cached);
  return cached;
};

const stateFromPage = (result: Record<string, any> = {}) => ({
  encounters: result.encounters || [],
  lastDoc: result.lastDoc || null,
  hasMore: Boolean(result.hasMore),
  isLoading: false,
  loadingMore: false,
  error: null,
  hasLoaded: true,
});

const appendPage = (
  previous: Record<string, any>,
  result: Record<string, any> = {},
) => {
  const seen = new Set(
    previous.encounters.map((encounter: any) => encounter.id),
  );
  const additions = (result.encounters || []).filter(
    (encounter: any) => !seen.has(encounter.id),
  );

  return {
    ...previous,
    encounters: [...previous.encounters, ...additions],
    lastDoc: result.lastDoc || null,
    hasMore: Boolean(result.hasMore),
    loadingMore: false,
    error: null,
    hasLoaded: true,
  };
};

export default function usePaginatedPlannerEncounterHistory({
  cacheKey = '',
  enabled = true,
  errorLabel = 'planner encounter history',
  fetchPage,
  identity,
  initialSeed = null,
  pageSize,
  cacheScope = '',
}: Record<string, any> = {}) {
  const [state, setState] = useState(EMPTY_STATE);
  const [refreshVersion, setRefreshVersion] = useState(0);
  const seedKey = initialSeed?.key || '';
  const resolvedCacheKey =
    cacheKey && identity
      ? `${cacheKey}:${identity}:${cacheScope || ''}:${pageSize || ''}:r${refreshVersion}`
      : '';
  const writeCache = useCallback(
    (page: Record<string, any>) => {
      writePlannerEncounterHistoryPageCache(resolvedCacheKey, page);
    },
    [resolvedCacheKey],
  );

  useEffect(() => {
    if (!enabled || !identity) {
      setState(EMPTY_STATE);
      return undefined;
    }

    const cached = readPlannerEncounterHistoryPageCache(resolvedCacheKey);
    if (cached) {
      setState(stateFromPage(cached));
      return undefined;
    }

    if (refreshVersion === 0 && initialSeed?.hasLoaded) {
      const seededState = stateFromPage(initialSeed);
      setState(seededState);
      writeCache(seededState);
      return undefined;
    }

    let cancelled = false;
    setState(prev => ({
      ...prev,
      isLoading: true,
      loadingMore: false,
      error: null,
    }));

    (async () => {
      try {
        const result = await fetchPage(identity, {pageSize});
        if (cancelled) return;
        const nextState = stateFromPage(result);
        setState(nextState);
        writeCache(nextState);
      } catch (err) {
        if (cancelled) return;
        console.error(`Error loading ${errorLabel}:`, err);
        setState({
          ...EMPTY_STATE,
          isLoading: false,
          error: err,
          hasLoaded: true,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    enabled,
    errorLabel,
    fetchPage,
    identity,
    initialSeed,
    pageSize,
    refreshVersion,
    resolvedCacheKey,
    seedKey,
    writeCache,
  ]);

  const loadMore = useCallback(async () => {
    if (!state.hasMore || state.loadingMore || !identity) return;
    setState(prev => ({...prev, loadingMore: true, error: null}));
    try {
      const result = await fetchPage(identity, {
        startAfter: state.lastDoc,
        pageSize,
      });
      setState(prev => {
        const nextState = appendPage(prev, result);
        writeCache(nextState);
        return nextState;
      });
    } catch (err) {
      console.error(`Error loading more ${errorLabel}:`, err);
      setState(prev => ({
        ...prev,
        loadingMore: false,
        error: err,
      }));
    }
  }, [
    errorLabel,
    fetchPage,
    identity,
    pageSize,
    state.hasMore,
    state.lastDoc,
    state.loadingMore,
    writeCache,
  ]);

  const refresh = useCallback(() => {
    setRefreshVersion(previous => previous + 1);
  }, []);

  return {
    encounters: state.encounters,
    hasMore: state.hasMore,
    isLoading: state.isLoading,
    loadingMore: state.loadingMore,
    error: state.error,
    hasLoaded: state.hasLoaded,
    loadMore,
    refresh,
  };
}
